import { useState } from "react";
import { MapPin, Phone, Mail, MessageCircle, Send, CheckCircle, Loader2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { LOCATIONS } from "@/data/siteData";
import { LocationsMap } from "@/components/LocationsMap";

export default function ContactPage() {
  const [form, setForm] = useState({ name: "", phone: "", email: "", message: "" });
  const [errors, setErrors] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const [sending, setSending] = useState(false);
  const [sendError, setSendError] = useState("");
  // Honeypot: hidden from real users, so anything here means a bot filled it in.
  const [company, setCompany] = useState("");

  const validate = () => {
    const errs = {};
    if (!form.name.trim()) errs.name = "Name is required";
    if (!form.phone.trim()) {
      errs.phone = "Phone number is required";
    } else if (!/^\+?\d{10,15}$/.test(form.phone.replace(/\s/g, ""))) {
      errs.phone = "Enter a valid phone number";
    }
    if (!form.email.trim()) {
      errs.email = "Email is required";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) {
      errs.email = "Enter a valid email address";
    }
    if (!form.message.trim()) errs.message = "Message is required";
    return errs;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const errs = validate();
    setErrors(errs);
    if (Object.keys(errs).length > 0) return;

    setSending(true);
    setSendError("");
    try {
      const res = await fetch("/api/contact", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...form, company }),
      });
      const data = await res.json().catch(() => ({}));

      if (!res.ok || !data.ok) {
        // The server re-validates, so surface any field errors it sends back.
        if (data.errors) setErrors(data.errors);
        setSendError(
          data.error ||
            "We couldn't send your message. Please try again, or reach us on +91 91797 48353."
        );
        return;
      }
      setSubmitted(true);
    } catch {
      setSendError(
        "Network error — please check your connection and try again, or call +91 91797 48353."
      );
    } finally {
      setSending(false);
    }
  };

  const handleChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => {
        const next = { ...prev };
        delete next[field];
        return next;
      });
    }
  };

  return (
    <div data-testid="contact-page">
      {/* Header */}
      <section className="py-12 sm:py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-xs uppercase tracking-[0.2em] font-medium text-orange-500 mb-3 animate-fade-in-up">
            Reach Out
          </p>
          <h1
            data-testid="contact-heading"
            className="font-heading text-4xl sm:text-5xl font-bold text-slate-900 tracking-tight mb-4 animate-fade-in-up animation-delay-100"
          >
            Contact Us
          </h1>
          <p className="text-base sm:text-lg text-slate-600 max-w-2xl mx-auto font-body leading-relaxed animate-fade-in-up animation-delay-200">
            Have questions? Want to book a visit? We'd love to hear from you.
          </p>
        </div>
      </section>

      {/* Form + Info */}
      <section className="py-12 sm:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
            {/* Form */}
            <div>
              <h2 className="font-heading text-2xl font-semibold text-slate-900 mb-6">
                Send Us a Message
              </h2>
              {submitted ? (
                <div
                  data-testid="contact-success-message"
                  className="bg-green-50 border border-green-200 rounded-2xl p-8 text-center animate-fade-in-up"
                >
                  <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-green-100 flex items-center justify-center">
                    <CheckCircle className="w-8 h-8 text-green-600" />
                  </div>
                  <h3 className="font-heading text-xl font-semibold text-green-800 mb-2">
                    Message Sent!
                  </h3>
                  <p className="text-sm text-green-700">
                    Thanks for reaching out. Our team will get back to you within 24 hours.
                  </p>
                  <button
                    data-testid="contact-send-another-btn"
                    onClick={() => {
                      setSubmitted(false);
                      setForm({ name: "", phone: "", email: "", message: "" });
                    }}
                    className="mt-6 text-sm text-green-600 hover:text-green-700 font-medium underline"
                  >
                    Send another message
                  </button>
                </div>
              ) : (
                <form onSubmit={handleSubmit} data-testid="contact-form" className="space-y-5 relative">
                  <div>
                    <label className="text-sm font-medium text-slate-700 mb-1.5 block">
                      Full Name
                    </label>
                    <Input
                      data-testid="contact-name-input"
                      placeholder="John Doe"
                      value={form.name}
                      onChange={(e) => handleChange("name", e.target.value)}
                      className={`h-11 rounded-xl ${errors.name ? "border-red-400 focus-visible:ring-red-400" : ""}`}
                    />
                    {errors.name && (
                      <p data-testid="contact-name-error" className="text-xs text-red-500 mt-1">{errors.name}</p>
                    )}
                  </div>
                  <div>
                    <label className="text-sm font-medium text-slate-700 mb-1.5 block">
                      Phone Number
                    </label>
                    <Input
                      data-testid="contact-phone-input"
                      placeholder="+91 91797 48353"
                      value={form.phone}
                      onChange={(e) => handleChange("phone", e.target.value)}
                      className={`h-11 rounded-xl ${errors.phone ? "border-red-400 focus-visible:ring-red-400" : ""}`}
                    />
                    {errors.phone && (
                      <p data-testid="contact-phone-error" className="text-xs text-red-500 mt-1">{errors.phone}</p>
                    )}
                  </div>
                  <div>
                    <label className="text-sm font-medium text-slate-700 mb-1.5 block">
                      Email Address
                    </label>
                    <Input
                      data-testid="contact-email-input"
                      type="email"
                      placeholder="john@example.com"
                      value={form.email}
                      onChange={(e) => handleChange("email", e.target.value)}
                      className={`h-11 rounded-xl ${errors.email ? "border-red-400 focus-visible:ring-red-400" : ""}`}
                    />
                    {errors.email && (
                      <p data-testid="contact-email-error" className="text-xs text-red-500 mt-1">{errors.email}</p>
                    )}
                  </div>
                  <div>
                    <label className="text-sm font-medium text-slate-700 mb-1.5 block">
                      Message
                    </label>
                    <Textarea
                      data-testid="contact-message-input"
                      placeholder="Tell us about your workspace needs..."
                      rows={4}
                      value={form.message}
                      onChange={(e) => handleChange("message", e.target.value)}
                      className={`rounded-xl ${errors.message ? "border-red-400 focus-visible:ring-red-400" : ""}`}
                    />
                    {errors.message && (
                      <p data-testid="contact-message-error" className="text-xs text-red-500 mt-1">{errors.message}</p>
                    )}
                  </div>
                  {/* Honeypot — hidden from people, irresistible to bots. */}
                  <div className="absolute left-[-9999px]" aria-hidden="true">
                    <label htmlFor="company">Company</label>
                    <input
                      id="company"
                      name="company"
                      type="text"
                      tabIndex={-1}
                      autoComplete="off"
                      value={company}
                      onChange={(e) => setCompany(e.target.value)}
                    />
                  </div>
                  {sendError && (
                    <div
                      data-testid="contact-error-message"
                      role="alert"
                      className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700"
                    >
                      {sendError}
                    </div>
                  )}
                  <button
                    type="submit"
                    disabled={sending}
                    data-testid="contact-submit-btn"
                    className="w-full rounded-full bg-orange-500 text-white font-medium px-6 py-3 hover:bg-orange-600 active:scale-[0.98] transition-all shadow-md shadow-orange-500/20 inline-flex items-center justify-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed disabled:active:scale-100"
                  >
                    {sending ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" />
                        Sending...
                      </>
                    ) : (
                      <>
                        <Send className="w-4 h-4" />
                        Send Message
                      </>
                    )}
                  </button>
                </form>
              )}
            </div>

            {/* Contact Info */}
            <div>
              <h2 className="font-heading text-2xl font-semibold text-slate-900 mb-6">
                Get in Touch
              </h2>
              <div className="space-y-5 mb-10">
                <a
                  href="tel:+919179748353"
                  data-testid="contact-phone-link"
                  className="flex items-center gap-4 bg-slate-50 rounded-xl p-4 border border-slate-100 hover:border-orange-200 transition-all"
                >
                  <div className="w-10 h-10 rounded-xl bg-orange-50 flex items-center justify-center flex-shrink-0">
                    <Phone className="w-5 h-5 text-orange-500" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-slate-900">Call Us</p>
                    <p className="text-sm text-slate-500">+91 91797 48353</p>
                  </div>
                </a>
                <a
                  href="mailto:contact@coffice24x7.in"
                  data-testid="contact-email-link"
                  className="flex items-center gap-4 bg-slate-50 rounded-xl p-4 border border-slate-100 hover:border-orange-200 transition-all"
                >
                  <div className="w-10 h-10 rounded-xl bg-orange-50 flex items-center justify-center flex-shrink-0">
                    <Mail className="w-5 h-5 text-orange-500" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-slate-900">Email Us</p>
                    <p className="text-sm text-slate-500">contact@coffice24x7.in</p>
                  </div>
                </a>
                <a
                  href="https://wa.me/919179748353"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="contact-whatsapp-btn"
                  className="flex items-center gap-4 bg-green-50 rounded-xl p-4 border border-green-200 hover:border-green-300 transition-all"
                >
                  <div className="w-10 h-10 rounded-xl bg-green-100 flex items-center justify-center flex-shrink-0">
                    <MessageCircle className="w-5 h-5 text-green-600" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-green-800">WhatsApp Us</p>
                    <p className="text-sm text-green-600">Quick response guaranteed</p>
                  </div>
                </a>
              </div>

              {/* All Addresses */}
              <h3 className="font-heading text-lg font-semibold text-slate-900 mb-4">
                Our Centers
              </h3>
              <div className="space-y-3">
                {LOCATIONS.map((loc) => (
                  <div
                    key={loc.id}
                    data-testid={`contact-address-${loc.id}`}
                    className="flex items-start gap-3 text-sm"
                  >
                    <MapPin className="w-4 h-4 text-orange-500 mt-0.5 flex-shrink-0" />
                    <div>
                      <span className="font-medium text-slate-900">{loc.name}: </span>
                      <span className="text-slate-500">{loc.address}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Map */}
      <section data-testid="contact-map-section" className="bg-slate-50 py-10 sm:py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-heading text-2xl font-semibold text-slate-900 mb-2">
            Find Us on the Map
          </h2>
          <p className="text-sm text-slate-600 mb-6">
            All {LOCATIONS.length} Coffice 24x7 centers across Pune. Click a pin for
            details and directions.
          </p>
          <div
            data-testid="contact-map"
            className="rounded-2xl overflow-hidden border border-slate-200 shadow-lg"
          >
            <LocationsMap className="h-80 sm:h-[28rem] w-full" />
          </div>
        </div>
      </section>
    </div>
  );
}
